import React, { useState } from 'react';
import { testAPI, settingsAPI } from '../services/api';
import SetupErrorMessage from './SetupErrorMessage';
import './TestPage.css';

function TestPage() {
  const [activeTab, setActiveTab] = useState('email');

  // Email test
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('Test Email from Outreach Engine');
  const [emailMessage, setEmailMessage] = useState('This is a test email to verify email sending is working correctly.');
  const [emailProvider, setEmailProvider] = useState('smtp');

  // SMS test
  const [smsTo, setSmsTo] = useState('');
  const [smsMessage, setSmsMessage] = useState('This is a test SMS from Outreach Engine.');

  // Preview
  const [emailPreview, setEmailPreview] = useState(null);
  const [smsPreview, setSmsPreview] = useState(null);

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);

  const handleEmailPreview = async () => {
    try {
      const response = await testAPI.previewEmail({
        subject: emailSubject,
        message: emailMessage
      });
      setEmailPreview(response.data.preview);
    } catch (err) {
      console.error('Preview failed:', err);
    }
  };

  const handleSmsPreview = async () => {
    try {
      const response = await testAPI.previewSMS({
        message: smsMessage
      });
      setSmsPreview(response.data.preview);
    } catch (err) {
      console.error('Preview failed:', err);
    }
  };

  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!emailTo) {
      setError('Please enter a recipient email address');
      return;
    }

    try {
      setLoading(true);
      const response = await testAPI.email({
        to: emailTo,
        subject: emailSubject,
        message: emailMessage,
        provider: emailProvider
      });
      setSuccess('Test email sent successfully! Check your inbox.');
    } catch (err) {
      const errorData = err.response?.data;
      let errorMessage = `Failed to send test email`;

      if (errorData) {
        // Enhanced error handling for SendGrid and Resend
        if ((emailProvider === 'sendgrid' || emailProvider === 'resend') && errorData.error_type) {
          errorMessage = errorData.error;

          // Show diagnostic suggestion for provider errors
          if (errorData.error_type === 'authorization_failed' ||
              errorData.error_type === 'authentication_failed' ||
              errorData.error_type === 'authorization_exception' ||
              errorData.error_type === 'authentication_exception') {

            const diagnosticHandler = emailProvider === 'sendgrid'
              ? handleSendGridDiagnostic
              : handleResendDiagnostic;

            const providerName = emailProvider === 'sendgrid' ? 'SendGrid' : 'Resend';

            setError(
              <div className="error-details">
                <p className="error-main">{errorMessage}</p>
                <div className="error-suggestions">
                  <p><strong>Suggestions:</strong></p>
                  <ul>
                    <li>Check your {providerName} API key in Settings</li>
                    <li>Ensure your API key has proper permissions</li>
                    <li>Verify your sender email/domain is authenticated in {providerName}</li>
                  </ul>
                  <button
                    onClick={diagnosticHandler}
                    className="diagnostic-btn"
                    disabled={diagnosticLoading}
                  >
                    {diagnosticLoading ? 'Running...' : `Run ${providerName} Diagnostic`}
                  </button>
                </div>
              </div>
            );
            return;
          }
        } else {
          errorMessage = errorData.error || errorMessage;
        }
      } else {
        errorMessage = `${errorMessage}: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSendGridDiagnostic = async () => {
    setDiagnosticLoading(true);
    setDiagnosticResults(null);

    try {
      const response = await settingsAPI.testSendGrid(emailTo);
      setDiagnosticResults(response.data.results);
      setShowDiagnostic(true);
    } catch (err) {
      setDiagnosticResults({
        diagnostic_results: [{
          status: 'error',
          message: `Diagnostic failed: ${err.response?.data?.error || err.message}`
        }]
      });
      setShowDiagnostic(true);
    } finally {
      setDiagnosticLoading(false);
    }
  };

  const handleResendDiagnostic = async () => {
    setDiagnosticLoading(true);
    setDiagnosticResults(null);

    try {
      const response = await settingsAPI.testResend(emailTo);
      setDiagnosticResults(response.data.results);
      setShowDiagnostic(true);
    } catch (err) {
      setDiagnosticResults({
        diagnostic_results: [{
          status: 'error',
          message: `Diagnostic failed: ${err.response?.data?.error || err.message}`
        }]
      });
      setShowDiagnostic(true);
    } finally {
      setDiagnosticLoading(false);
    }
  };

  const handleSendTestSMS = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!smsTo) {
      setError('Please enter a recipient phone number');
      return;
    }

    try {
      setLoading(true);
      await testAPI.sms({
        to: smsTo,
        message: smsMessage
      });
      setSuccess('Test SMS sent successfully! Check your phone.');
    } catch (err) {
      setError(`Failed to send test SMS: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="test-page">
      <div className="test-header">
        <h1 className="test-title">Test Email & SMS</h1>
        <p className="test-subtitle">Verify your email and SMS configuration before sending to buyers</p>
      </div>

      {error && <div className="alert alert-error">{typeof error === 'string' ? <SetupErrorMessage message={error} /> : error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Diagnostic Modal */}
      {showDiagnostic && diagnosticResults && (
        <div className="diagnostic-modal">
          <div className="diagnostic-content">
            <div className="diagnostic-header">
              <h3 className="diagnostic-title">
                {emailProvider === 'sendgrid' ? 'SendGrid' : emailProvider === 'resend' ? 'Resend' : 'Provider'} Diagnostic Results
              </h3>
              <button className="close-btn" onClick={() => setShowDiagnostic(false)}>
                ✕
              </button>
            </div>
            <div className="diagnostic-results">
              {diagnosticResults.diagnostic_results.map((result, index) => (
                <div key={index} className={`diagnostic-item ${result.status}`}>
                  <span className={`diagnostic-icon ${result.status}`}>
                    {result.status === 'success' && '✓'}
                    {result.status === 'error' && '⚠'}
                    {result.status === 'info' && 'ℹ'}
                  </span>
                  <div className="diagnostic-message">
                    {result.message}
                  </div>
                </div>
              ))}
            </div>

            {diagnosticResults.troubleshooting && diagnosticResults.troubleshooting.length > 0 && (
              <div className="troubleshooting-steps">
                <h4>Troubleshooting Steps:</h4>
                <ol>
                  {diagnosticResults.troubleshooting.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'email' ? 'active' : ''}`}
          onClick={() => setActiveTab('email')}
        >
          Email Test
        </button>
        <button
          className={`tab-button ${activeTab === 'sms' ? 'active' : ''}`}
          onClick={() => setActiveTab('sms')}
        >
          SMS Test
        </button>
      </div>

      {/* Email Test */}
      {activeTab === 'email' && (
        <>
          <div className="test-form">
            <h3>Send Test Email</h3>
            <form onSubmit={handleSendTestEmail}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Recipient Email *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    placeholder="test@example.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email Provider</label>
                  <select
                    className="form-select"
                    value={emailProvider}
                    onChange={(e) => setEmailProvider(e.target.value)}
                  >
                    <option value="smtp">SMTP</option>
                    <option value="resend">Resend</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Subject</label>
                <input
                  type="text"
                  className="form-input"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea
                  className="form-textarea"
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Email message"
                  rows="6"
                />
              </div>

              <div className="button-group">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Test Email'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleEmailPreview}
                >
                  Preview
                </button>
              </div>
            </form>
          </div>

          {emailPreview && (
            <div className="preview-section">
              <div className="preview-header">
                <h4 className="preview-title">Email Preview</h4>
              </div>
              <div className="preview-content">
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Subject:</strong> {emailPreview.subject}
                </div>
                <div>
                  <strong>Message:</strong>
                  {emailPreview.message}
                </div>
                {emailPreview.sample_data && (
                  <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    <strong>Sample data used:</strong>
                    <pre style={{ marginTop: '0.5rem' }}>
                      {JSON.stringify(emailPreview.sample_data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* SMS Test */}
      {activeTab === 'sms' && (
        <>
          <div className="card">
            <h3 className="card-title">Send Test SMS</h3>
            <form onSubmit={handleSendTestSMS}>
              <div className="form-group">
                <label className="form-label">Recipient Phone Number *</label>
                <input
                  type="tel"
                  className="form-input"
                  value={smsTo}
                  onChange={(e) => setSmsTo(e.target.value)}
                  placeholder="+1234567890"
                  required
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  Use E.164 format (e.g., +1234567890)
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea
                  className="form-textarea"
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  placeholder="SMS message"
                  rows="4"
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  {smsMessage.length} characters ({Math.ceil(smsMessage.length / 160)} SMS)
                </small>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Test SMS'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleSmsPreview}
                >
                  Preview
                </button>
              </div>
            </form>
          </div>

          {smsPreview && (
            <div className="card">
              <h3 className="card-title">SMS Preview</h3>
              <div style={{ backgroundColor: 'var(--surface-subtle)', padding: '1rem', borderRadius: '6px' }}>
                <pre style={{ whiteSpace: 'pre-wrap' }}>
                  {smsPreview.message}
                </pre>
                <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  <div>Characters: {smsPreview.char_count}</div>
                  <div>SMS Count: {smsPreview.sms_count}</div>
                </div>
                {smsPreview.sample_data && (
                  <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    <strong>Sample data used:</strong>
                    <pre style={{ marginTop: '0.5rem' }}>
                      {JSON.stringify(smsPreview.sample_data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Help */}
      <div className="card help-card">
        <h3 className="card-title">How to Use</h3>
        <ol style={{ paddingLeft: '1.5rem', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
          <li>Make sure you have configured your API keys in Settings</li>
          <li>Enter your own email or phone number as the recipient</li>
          <li>Customize the message if needed</li>
          <li>Click Preview to see how the message will look</li>
          <li>Click Send to test the actual delivery</li>
          <li>Check your email inbox or phone to verify delivery</li>
        </ol>
        <div className="alert alert-info" style={{ marginTop: '1rem' }}>
          <strong>Tip:</strong> Use placeholders like {'{business_name}'}, {'{industry}'}, and {'{location}'}
          in your messages. They will be replaced with actual buyer data when sending to buyers.
        </div>
      </div>
    </div>
  );
}

export default TestPage;
